import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { getClients, Client } from '../../services/api';
import { createLicense, License, LicenseCreateRequest } from '../../services/onpremApi';

// Feature flags a license can enable. Keys match what the on-prem backend
// reads from the license JWT; labels are what CDP staff see.
export const LICENSE_FEATURES: { key: string; label: string }[] = [
  { key: 'quotas', label: 'Cuotas de impresión' },
  { key: 'rules', label: 'Reglas de impresión' },
  { key: 'pull_print', label: 'Pull print (liberación segura)' },
  { key: 'color_pin_release', label: 'PIN de color' },
  { key: 'email_print', label: 'Impresión por email' },
  { key: 'scan', label: 'Flujos de escaneo' },
  { key: 'chargeback', label: 'Chargeback / centros de costo' },
];

// Presets only pre-fill the form; every value stays editable and the
// backend stores plan as a free string with no enforcement by plan.
export const PLAN_PRESETS: Record<
  string,
  { label: string; maxDevices: number; maxUsers: number; features: string[] }
> = {
  starter: {
    label: 'Starter',
    maxDevices: 25,
    maxUsers: 50,
    features: ['quotas', 'rules', 'pull_print'],
  },
  professional: {
    label: 'Professional',
    maxDevices: 100,
    maxUsers: 200,
    features: ['quotas', 'rules', 'pull_print', 'color_pin_release', 'email_print', 'scan'],
  },
  enterprise: {
    label: 'Enterprise',
    maxDevices: 500,
    maxUsers: 2000,
    features: LICENSE_FEATURES.map((f) => f.key),
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (license: License) => void;
}

const CreateLicenseDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [clientOptions, setClientOptions] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [client, setClient] = useState<Client | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [plan, setPlan] = useState<string>('professional');
  const [maxDevices, setMaxDevices] = useState<number>(PLAN_PRESETS.professional.maxDevices);
  const [maxUsers, setMaxUsers] = useState<number>(PLAN_PRESETS.professional.maxUsers);
  const [features, setFeatures] = useState<string[]>(PLAN_PRESETS.professional.features);
  const [termMonths, setTermMonths] = useState<number>(12);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<License | null>(null);
  const [copied, setCopied] = useState(false);

  // Client search — debounced lookup against the clients API.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await getClients(0, 20, clientSearch || undefined);
        if (!cancelled) setClientOptions(res.items);
      } catch {
        if (!cancelled) setClientOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [clientSearch, open]);

  const applyPreset = (key: string) => {
    setPlan(key);
    const preset = PLAN_PRESETS[key];
    if (preset) {
      setMaxDevices(preset.maxDevices);
      setMaxUsers(preset.maxUsers);
      setFeatures(preset.features);
    }
  };

  const toggleFeature = (key: string) =>
    setFeatures((prev) => (prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]));

  const canSubmit = useMemo(
    () =>
      !!client &&
      organizationName.trim().length > 0 &&
      maxDevices > 0 &&
      maxUsers > 0 &&
      termMonths > 0 &&
      !submitting,
    [client, organizationName, maxDevices, maxUsers, termMonths, submitting],
  );

  const handleSubmit = async () => {
    if (!client) return;
    setSubmitting(true);
    setError(null);
    const body: LicenseCreateRequest = {
      client_id: client.customer_id,
      organization_name: organizationName.trim(),
      plan,
      max_devices: maxDevices,
      max_users: maxUsers,
      features,
      term_months: termMonths,
      notes: notes.trim() || null,
    };
    try {
      const license = await createLicense(body);
      setIssued(license);
      onCreated(license);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'No se pudo emitir la licencia');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.license_key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const reset = () => {
    setClient(null);
    setClientSearch('');
    setOrganizationName('');
    applyPreset('professional');
    setTermMonths(12);
    setNotes('');
    setError(null);
    setIssued(null);
    setCopied(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {issued ? (
        <>
          <DialogTitle>Licencia emitida</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              Copie la clave ahora. Se activa en una sola máquina; para reinstalar hay que
              emitir una licencia nueva.
            </Alert>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
              }}
            >
              <Typography component="code" sx={{ flex: 1, fontSize: '1.05rem', wordBreak: 'break-all' }}>
                {issued.license_key}
              </Typography>
              <Tooltip title={copied ? 'Copiada' : 'Copiar clave'}>
                <IconButton aria-label="Copiar clave" onClick={handleCopy} size="small">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {issued.organization_name} · plan {issued.plan} · {issued.max_devices} equipos ·{' '}
              {issued.max_users} usuarios · vence {issued.expires_at.slice(0, 10)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained">
              Listo
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Emitir licencia on-prem</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={clientOptions}
                  value={client}
                  onChange={(_e, value) => {
                    setClient(value);
                    if (value) setOrganizationName(value.razon_social);
                  }}
                  onInputChange={(_e, value) => setClientSearch(value)}
                  getOptionLabel={(c) => `${c.customer_id} — ${c.razon_social}`}
                  isOptionEqualToValue={(a, b) => a.customer_id === b.customer_id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" required autoFocus />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Organización (como aparece en la licencia)"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Plan"
                  value={plan}
                  onChange={(e) => applyPreset(e.target.value)}
                  fullWidth
                >
                  {Object.entries(PLAN_PRESETS).map(([key, preset]) => (
                    <MenuItem key={key} value={key}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  label="Máx. equipos"
                  type="number"
                  value={maxDevices}
                  onChange={(e) => setMaxDevices(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  label="Máx. usuarios"
                  type="number"
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Funciones habilitadas
                </Typography>
                <FormGroup row>
                  {LICENSE_FEATURES.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          checked={features.includes(f.key)}
                          onChange={() => toggleFeature(f.key)}
                          size="small"
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Vigencia (meses)"
                  type="number"
                  value={termMonths}
                  onChange={(e) => setTermMonths(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  label="Notas"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
              Emitir licencia
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CreateLicenseDialog;
